import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import Page from 'src/components/Page';
import CustomDialog from 'src/components/CustomDialog';
import Broadcast from 'src/api/broadcast';
import alertSnackbarActions from 'src/store/actions/alertSnackbarActions';

const AUDIENCES = [
  { value: 'all_riders', label: 'All Riders' },
  { value: 'all_drivers', label: 'All Drivers' },
  { value: 'both', label: 'Both (All Users)' }
];

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  editor: {
    marginTop: theme.spacing(2),
    '& .ql-container': {
      minHeight: 180,
      fontSize: 15
    }
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3)
  }
}));

// Quill represents "empty" as <p><br></p>; strip tags to check for real content.
const isBodyEmpty = html => !html || !html.replace(/<[^>]*>/g, '').trim();

const BroadcastView = ({ setOpen, setMessage, setSeverity }) => {
  const classes = useStyles();

  const [audience, setAudience] = useState('all_riders');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Live recipient count whenever the audience changes.
  useEffect(() => {
    let active = true;
    setCountLoading(true);
    Broadcast.getRecipientCount(audience)
      .then(res => {
        if (active) setRecipientCount(res.data.data.count);
      })
      .catch(() => {
        if (active) setRecipientCount(null);
      })
      .finally(() => {
        if (active) setCountLoading(false);
      });
    return () => {
      active = false;
    };
  }, [audience]);

  const notify = (severity, message) => {
    setSeverity(severity);
    setMessage(message);
    setOpen(true);
  };

  const validate = () => {
    if (!subject.trim()) {
      notify('error', 'Subject is required');
      return false;
    }
    if (isBodyEmpty(body)) {
      notify('error', 'Message body is required');
      return false;
    }
    return true;
  };

  const handleTest = () => {
    if (!validate()) return;
    setTesting(true);
    Broadcast.sendTest({ subject: subject.trim(), body })
      .then(() => notify('success', 'Test email sent to your address'))
      .catch(err =>
        notify('error', err?.response?.data?.errors?.[0] || 'Failed to send test')
      )
      .finally(() => setTesting(false));
  };

  const handleSendClick = () => {
    if (!validate()) return;
    setDialogOpen(true);
  };

  const confirmSend = () => {
    setDialogOpen(false);
    setSending(true);
    Broadcast.sendBroadcast({ subject: subject.trim(), body, audience })
      .then(res => {
        notify('success', res?.data?.message || 'Broadcast queued');
        setSubject('');
        setBody('');
      })
      .catch(err =>
        notify(
          'error',
          err?.response?.data?.errors?.[0] || 'Failed to send broadcast'
        )
      )
      .finally(() => setSending(false));
  };

  const audienceLabel =
    AUDIENCES.find(a => a.value === audience)?.label || audience;

  return (
    <Page className={classes.root} title="Broadcast Email">
      <Container maxWidth="md">
        <Card>
          <CardHeader
            title="Broadcast Email"
            subheader="Send an email to all riders, all drivers, or everyone."
          />
          <Divider />
          <CardContent>
            <FormControl variant="outlined" fullWidth margin="normal">
              <InputLabel id="audience-label">Audience</InputLabel>
              <Select
                labelId="audience-label"
                label="Audience"
                value={audience}
                onChange={e => setAudience(e.target.value)}
              >
                {AUDIENCES.map(a => (
                  <MenuItem key={a.value} value={a.value}>
                    {a.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mb={1}>
              <Chip
                color="primary"
                variant="outlined"
                label={
                  countLoading
                    ? 'Counting recipients…'
                    : `${
                        recipientCount === null
                          ? '—'
                          : recipientCount.toLocaleString()
                      } recipient(s)`
                }
              />
            </Box>

            <TextField
              fullWidth
              label="Subject"
              margin="normal"
              variant="outlined"
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />

            <Typography variant="body2" color="textSecondary">
              Message
            </Typography>
            <Box className={classes.editor}>
              <ReactQuill
                theme="snow"
                value={body}
                onChange={setBody}
                modules={QUILL_MODULES}
              />
            </Box>

            <Box className={classes.actions}>
              <Button
                variant="outlined"
                color="primary"
                disabled={testing}
                onClick={handleTest}
              >
                {testing ? 'Sending test…' : 'Send test to myself'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={sending}
                onClick={handleSendClick}
              >
                {sending ? 'Sending…' : 'Send broadcast'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <CustomDialog
        open={dialogOpen}
        title="Send broadcast?"
        message={`This will email "${subject.trim()}" to ${
          recipientCount === null ? 'all' : recipientCount.toLocaleString()
        } recipient(s) in "${audienceLabel}". This cannot be undone.`}
        handleDialogClose={() => setDialogOpen(false)}
        action={confirmSend}
      />
    </Page>
  );
};

BroadcastView.propTypes = {
  setOpen: PropTypes.func,
  setMessage: PropTypes.func,
  setSeverity: PropTypes.func
};

const { setOpen, setMessage, setSeverity } = alertSnackbarActions;

const mapDispatchToProps = {
  setOpen,
  setMessage,
  setSeverity
};

export default connect(null, mapDispatchToProps)(BroadcastView);
